import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, generatePath, useNavigate } from 'react-router-dom';

import { DataContext, UserContext } from '../App';
import { Button, DeletePopup, PushToTvPopup } from '../components';
import Frame from '../layout/Frame';
import { Dashboard, StateAction } from '../types/types';
import { AppRoute, ButtonType } from '../types/enums';
import { deleteDashboard } from '../requests/requests';
import useErrorHandler from '../hooks/useErrorHandler';
import useFilter from '../hooks/useFilter';
import AddNew from '../components/AddNew/AddNew';
import LimitReachedPopup from '../components/LimitReachedPopup/LimitReachedPopup';
import { DashboardInfo } from '../components/DashboardInfo/DashboardInfo';
import Panel from '../layout/Panel';
import { getDashboardUrlById } from '../utils/common';

const Dashboards = () => {
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const { dashboards, refreshDashboards } = useContext(DataContext);
  const { user } = useContext(UserContext);
  const [selectedForDelete, setSelectedForDelete] = useState<Dashboard>();
  const [selectedForPush, setSelectedForPush] = useState<Dashboard>();
  const [query, setQuery] = useState('');
  const filteredDashboards = useFilter(dashboards, query);
  const [showLimitPopup, setShowLimitPopup] = useState(false);

  const canAddNewDashboard = useMemo(
    () => dashboards.length < (user?.plan.dashboards ?? 0),
    [dashboards, user]
  );

  useEffect(() => {
    if (dashboards.length < 1) {
      refreshDashboards();
    }
  }, []);

  const onDelete = useCallback(() => {
    if (selectedForDelete?.id) {
      deleteDashboard(selectedForDelete.id)
        .then(() => {
          refreshDashboards();
        }, errorHandler)
        .finally(() => {
          setSelectedForDelete(undefined);
        });
    }
  }, [selectedForDelete]);

  return (
    <Frame>
      <Panel>
        <h1>Dashboards</h1>
        <p>
          Dashboards display the status information of your websites. Other tools call them „status
          pages”. Use multiple dashboards to display different groups of website statuses internally
          to your team and publicly to your clients.
        </p>
        <DashboardInfo
          actionRenderer={Actions({ delete: setSelectedForDelete, push: setSelectedForPush })}
          data={filteredDashboards}
          rowHeaderRenderer={Info}
          search={query}
          setSearch={setQuery}
          renderAction={
            <AddNew
              title="Add new dashboard"
              action={
                canAddNewDashboard
                  ? () => navigate(AppRoute.DASHBOARD_NEW)
                  : () => setShowLimitPopup(true)
              }
            />
          }
        />
        {!!selectedForDelete && (
          <DeletePopup
            onCancel={() => setSelectedForDelete(undefined)}
            onDelete={onDelete}
            title={`You'll delete "${selectedForDelete?.name}" permanently.`}
          />
        )}
        {!!selectedForPush && (
          <PushToTvPopup onCancel={() => setSelectedForPush(undefined)} id={selectedForPush.id} />
        )}
        {!!showLimitPopup && <LimitReachedPopup hide={() => setShowLimitPopup(false)} />}
      </Panel>
    </Frame>
  );
};

const Info: React.FC<Dashboard> = ({ name, description, id }) => (
  <>
    <strong>
      <a href={getDashboardUrlById(id)} target="_blank" rel="noreferrer">
        {name}
      </a>
    </strong>
    <div>{description}</div>
  </>
);

const Actions: (actions: {
  delete: StateAction<Dashboard | undefined>;
  push: StateAction<Dashboard | undefined>;
}) => React.FC<Dashboard> = (actions) => (dashboard) => {
  return (
    <>
      <Button type={ButtonType.PRIMARY} spaceSaving small action={() => actions.push(dashboard)}>
        Share
      </Button>
      <NavLink to={generatePath(AppRoute.DASHBOARD_EDIT, { id: dashboard.id })}>
        <Button type={ButtonType.GHOST} spaceSaving small>
          edit
        </Button>
      </NavLink>
      <Button action={() => actions.delete(dashboard)} type={ButtonType.GHOST} spaceSaving small>
        delete
      </Button>
    </>
  );
};

export default Dashboards;
